export const CANCELLATION_REASONS = [
    'sold_vehicle',
    'changed_vehicle',
    'moved_away',
    'bereavement',
    'financial_hardship',
    'unhappy_with_service',
    'other',
] as const;
export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

export const REQUESTING_PARTIES = ['customer', 'dealer', 'motorplan'] as const;
export type CancellationRequestingParty = (typeof REQUESTING_PARTIES)[number];

export const SERVICE_HISTORY_SOURCES = ['dealer_contact', 'thirty_day_rule', 'case_reference'] as const;
export type ServiceHistorySource = (typeof SERVICE_HISTORY_SOURCES)[number];

export type CancellationUnavailableCode =
    | 'not_permitted'
    | 'cancellation_in_progress'
    | 'state_not_cancellable'
    | 'campaign_not_cancellable'
    | 'cthf_no_refund_unsupported';

export interface CancellationEstimate {
    asAt: string;
    fundBalance: string;
    cancellationFee: string;
    feeCharged: string;
    feeWaived: boolean;
    feeWaivedReason: 'cooling_off' | null;
    coolingOffEndsOn: string | null;
    refund: string;
    noCustomerRefund: boolean;
}

export interface CancelAction {
    available: boolean;
    reasonCode: CancellationUnavailableCode | null;
    message: string | null;
    estimate: CancellationEstimate | null;
    estimateUnavailable: boolean;
    requiresServiceHistory: boolean;
}

export type CancellationAwaitingReason =
    | 'negative_balance'
    | 'elemis_not_eligible'
    | 'elemis_already_cancelled'
    | 'elemis_cancelled_elsewhere'
    | 'elemis_agreement_unlinked'
    | 'refund_failed'
    | 'integration_fault';

export type CancellationProgress =
    | { kind: 'awaiting_clearance' }
    | { kind: 'settling'; refundPending?: boolean }
    | { kind: 'awaiting_motorplan'; reason: CancellationAwaitingReason };

export type CompletableReason = Exclude<CancellationAwaitingReason, 'elemis_cancelled_elsewhere' | 'elemis_agreement_unlinked'>;
export type CompletionOption = 'cancel_with_negative_balance' | 'close_in_artemis' | 'resend' | 'retry_refund';

export type RefundOwedStatus = 'owed' | 'in_payment' | 'paid' | 'failed';

export interface CancellationPanelData {
    reason: CancellationReason;
    note: string | null;
    requestingParty: CancellationRequestingParty;
    caseReference: string | null;
    cancelledBy: string;
    requestedAt: string;
    cancelledAt: string | null;
    figuresFinal: boolean;
    feeCharged: string;
    feeWaived: boolean;
    feeWaivedReason: 'cooling_off' | null;
    noCustomerRefund: boolean;
    refundOwed: string | null;
    refundStatus: RefundOwedStatus | null;
    closedInArtemis: boolean;
    completionNote: string | null;
    completedBy: string | null;
}

export interface CancellationRecord extends CancellationPanelData {
    id: string;
    progress: CancellationProgress;
}

export interface CancelAgreementPayload {
    reason: string;
    note: string | null;
    requesting_party: string;
    case_reference: string | null;
    service_history_confirmed?: boolean;
    service_history_source?: string;
    service_history_detail?: string | null;
}

export interface CancellationStarted {
    id: string;
    status: string;
}
